import { useState, useCallback, useEffect } from 'react';

const useTableData = ({
  apiService,
  method = 'getList',
  queryParams = {},
  resourceId,
  customConfig = {},
}) => {
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { pageIndex, pageSize, filters, sorting } = queryParams;

  const transformParams = useCallback(
    params => {
      if (customConfig?.transformParams) {
        return customConfig.transformParams(params);
      }

      if (!customConfig.enablePagination) {
        const queryParams = new URLSearchParams();

        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach(v => {
                if (v !== undefined && v !== null && v !== '') {
                  queryParams.append(key, v);
                }
              });
            } else if (value !== undefined && value !== null && value !== '') {
              queryParams.append(key, value);
            }
          });
        }

        // Sorting
        if (params.sorting?.length) {
          params.sorting.forEach(({ id, desc }) => {
            const sortKey = desc ? `-${id}` : id;
            queryParams.append('sort', sortKey);
          });
        }

        return queryParams;
      }

      const transformedParams = new URLSearchParams();

      if (params.pageIndex !== undefined) {
        transformedParams.append('page', params.pageIndex + 1);
      }
      if (params.pageSize) {
        transformedParams.append('page_size', params.pageSize);
      }

      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => {
              if (v !== undefined && v !== null && v !== '') {
                transformedParams.append(key, v);
              }
            });
          } else if (typeof value === 'string' && value.includes('\n')) {
            // MULTI_TEXT
            value
              .split('\n')
              .map(v => v.trim())
              .filter(v => v !== '')
              .forEach(v => {
                transformedParams.append(key, v);
              });
          } else if (value !== undefined && value !== null && value !== '') {
            transformedParams.append(key, value);
          }
        });
      }

      // Sorting
      if (params.sorting?.length) {
        params.sorting.forEach(({ id, desc }) => {
          const sortKey = desc ? `-${id}` : id;
          transformedParams.append('sort', sortKey);
        });
      }

      return transformedParams;
    },
    [customConfig.enablePagination, customConfig.transformParams],
  );

  const transformResponse = useCallback(
    response => {
      if (customConfig.transformResponse) {
        return customConfig.transformResponse(response);
      }

      if (Array.isArray(response)) {
        return {
          data: response,
          totalItems: response.length,
        };
      }

      if (response?.results && typeof response?.count === 'number') {
        return {
          data: response.results,
          totalItems: response.count,
        };
      }

      // 단일 객체 응답 처리
      if (typeof response === 'object' && response !== null) {
        const results = response.results || response;
        return {
          data: Array.isArray(results) ? results : [results],
          totalItems:
            response.count || (Array.isArray(results) ? results.length : 1),
        };
      }

      return {
        data: [],
        totalItems: 0,
      };
    },
    [customConfig.transformResponse],
  );

  const fetchData = useCallback(
    async (isMounted = true) => {
      setLoading(true);
      setError(null);

      try {
        const params = transformParams({
          pageIndex,
          pageSize,
          filters,
          sorting,
        });

        const response = await (resourceId
          ? apiService[method](resourceId, params)
          : apiService[method](params));

        const { data: transformedData, totalItems: transformedTotal } =
          transformResponse(response);

        if (isMounted) {
          setData(transformedData);
          setTotalItems(transformedTotal);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage =
            err.response?.data?.message || err.message || 'An error occurred';
          setError(errorMessage);
          setData([]);
          setTotalItems(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    },
    [
      apiService,
      method,
      resourceId,
      JSON.stringify({ pageIndex, pageSize, filters, sorting }),
      JSON.stringify(customConfig),
    ],
  );

  useEffect(() => {
    let isMounted = true;
    fetchData(isMounted);

    return () => {
      isMounted = false;
    };
  }, [fetchData]);

  return {
    data,
    totalItems,
    loading,
    error,
    setData,
    refetch: () => fetchData(true),
  };
};

export default useTableData;
